import { useEffect, useState } from 'react';
import { X, Bell, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Componente Toast para mostrar notificaciones emergentes
 */
export function NotificationToast({ notification, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Animación de entrada
    setIsVisible(true);

    // Auto-cerrar después de 5 segundos
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Esperar a que termine la animación
  };

  const handleClick = () => {
    if (notification.link) {
      navigate(notification.link);
      handleClose();
    }
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'new_order':
      case 'payment_uploaded':
        return <Bell className="h-5 w-5 text-sky-600 dark:text-sky-400" />;
      case 'payment_confirmed':
      case 'order_delivered':
        return <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'order_cancelled':
        return <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
      default:
        return <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'new_order':
      case 'payment_uploaded':
        return 'from-sky-50 to-blue-50 dark:from-sky-950/50 dark:to-blue-950/50 border-sky-200 dark:border-sky-800';
      case 'payment_confirmed':
      case 'order_delivered':
        return 'from-green-50 to-emerald-50 dark:from-green-950/50 dark:to-emerald-950/50 border-green-200 dark:border-green-800';
      case 'order_cancelled':
        return 'from-red-50 to-rose-50 dark:from-red-950/50 dark:to-rose-950/50 border-red-200 dark:border-red-800';
      default:
        return 'from-white to-slate-50 dark:from-zinc-900 dark:to-zinc-800 border-slate-200 dark:border-zinc-700';
    }
  };

  return (
    <div
      className={`transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div
        onClick={handleClick}
        className={`relative flex items-start gap-3 rounded-2xl border-2 bg-gradient-to-br p-4 shadow-lg backdrop-blur-sm ${getBackgroundColor()} ${
          notification.link ? 'cursor-pointer hover:shadow-xl' : ''
        } max-w-sm`}
      >
        {/* Icono */}
        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
            {notification.title}
          </h4>
          <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
            {notification.message}
          </p>
          {notification.link && (
            <p className="text-xs text-sky-600 dark:text-sky-400 mt-1 font-medium">
              Click para ver detalles →
            </p>
          )}
        </div>

        {/* Botón cerrar */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className="flex-shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-200/50 dark:hover:bg-zinc-700/50 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Barra de progreso */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-200 dark:bg-zinc-700 rounded-b-2xl overflow-hidden">
          <div
            className="h-full bg-sky-500 dark:bg-sky-400 animate-progress"
            style={{
              animation: 'progress 5s linear forwards',
            }}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Contenedor de notificaciones Toast
 */
export function NotificationToastContainer({ notifications, onRemove }) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
      <div className="pointer-events-auto space-y-3">
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onClose={() => onRemove(notification.id)}
          />
        ))}
      </div>
    </div>
  );
}
